import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccess } from "../api/access";
import { api, HttpError } from "../api/client";
import type { Answer, ScheduleEvent, ScheduleKind } from "../api/types";
import { CopyEventDialog } from "../components/CopyEventDialog";
import { EventForm } from "../components/EventForm";
import { endOfMonth, sameDay, startOfMonth } from "../components/format";

/**
 * The Schedule — the hunting team's Events.
 *
 * <p>Three views over the same data. <b>Upcoming</b> is the default and the
 * one the screen exists for: it puts "what have I not answered?" in reading
 * order, which a grid of mostly-empty cells does not. <b>Month</b> answers the
 * other question — "how does November actually look?" — which a list of
 * fourteen rows genuinely cannot. <b>Past</b> looks back.
 *
 * <p>The month grid borrows the photo Calendar's chrome deliberately (same
 * card, same header, same Mon-first day row) so the two read as one product.
 * It is <em>not</em> the Calendar: cells hold Events, not frame counts, and the
 * nav keeps the two words apart exactly as CONTEXT.md requires. Cells are sized
 * so a title is legible in place rather than a dot you have to click to
 * identify — a schedule you cannot read at a glance is a list with extra steps.
 *
 * <p>Every Event shows its tally and your own answer inline. Answering is one
 * click from wherever you found it: making people open an Event first would be
 * a step between them and the only action most visits are for.
 */

type View = "upcoming" | "month" | "past";

/** Event pills per cell before the rest collapse into "+N more". */
const PILLS_PER_DAY = 3;

interface Props {
  /**
   * Called whenever this screen changes how many Events are unanswered. The
   * nav badge lives in the shell and would otherwise only refresh on a route
   * change — leaving it reading "4" directly above a list that says three.
   */
  onPendingChanged: () => void;
}

export function ScheduleScreen({ onPendingChanged }: Props) {
  const [view, setView] = useState<View>("upcoming");
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [kinds, setKinds] = useState<ScheduleKind[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ScheduleEvent | null>(null);
  const [copying, setCopying] = useState<ScheduleEvent | null>(null);
  const [creating, setCreating] = useState(false);
  // Confirmation after a copy. Six new Events appearing in a long list is easy
  // to miss, and "did that work?" is the obvious next thought.
  const [notice, setNotice] = useState<string | null>(null);
  // Month view: which month the grid shows, and which day's Events are listed
  // beneath it. Null means no day picked yet — the grid alone is the answer.
  const [monthCursor, setMonthCursor] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list =
        view === "upcoming"
          ? await api.listEvents({ from: todayISO() })
          : view === "month"
            ? await api.listEvents({
                from: toISO(startOfMonth(monthCursor)),
                to: toISO(endOfMonth(monthCursor)),
              })
            : // A year back is enough to answer "who came to the opening
              // morning?" without dragging the whole archive over the wire.
              await api.listEvents({ from: aYearAgoISO(), to: yesterdayISO() });
      setEvents(view === "past" ? [...list].reverse() : list);
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "The Schedule is for hunting team members. Ask an admin if you should have access."
          : "Could not load the Schedule. Try again in a moment.",
      );
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [view, monthCursor]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Kinds only change when an Event is created, so this is fetched once and
  // refreshed on save rather than alongside every list load.
  const loadKinds = useCallback(async () => {
    try {
      setKinds(await api.listEventKinds());
    } catch {
      // Autocomplete is a convenience — a free-text kind still works without it.
    }
  }, []);

  useEffect(() => {
    void loadKinds();
  }, [loadKinds]);

  const replace = useCallback((saved: ScheduleEvent) => {
    setEvents((prev) => prev.map((e) => (e.eventId === saved.eventId ? saved : e)));
  }, []);

  /**
   * Drop a deleted Event from the list rather than re-fetching. Unlike a save,
   * there is no question of whether it still belongs in this view — it does not
   * exist. The notice says what went, because an unexplained gap in a list is
   * indistinguishable from a bug.
   */
  const remove = useCallback(
    (gone: ScheduleEvent) => {
      setEvents((prev) => prev.filter((e) => e.eventId !== gone.eventId));
      setNotice(`Deleted “${gone.title}” on ${fmtDay(gone.date)}.`);
      // An Event you had not answered stops being something you owe.
      onPendingChanged();
    },
    [onPendingChanged],
  );

  const onSaved = useCallback(() => {
    setEditing(null);
    setCreating(false);
    void loadKinds();
    // Re-read rather than splicing the saved Event in: it may not belong in the
    // current view at all — a date moved into the past while looking at
    // upcoming — and a row the filter says should not be there is worse than a
    // second request.
    void refresh();
    // A new Event is one more thing its creator has not answered.
    onPendingChanged();
  }, [loadKinds, refresh, onPendingChanged]);

  const pending = useMemo(
    () => events.filter((e) => !e.cancelled && !e.myResponse).length,
    [events],
  );

  // Events bucketed by local day, so the grid can look each cell up in O(1)
  // rather than filtering the month per cell.
  const byDay = useMemo(() => {
    const m = new Map<string, ScheduleEvent[]>();
    for (const e of events) {
      const list = m.get(e.date);
      if (list) list.push(e);
      else m.set(e.date, [e]);
    }
    // Within a day, earliest gathering first; Events with no time lead, since
    // "sometime that day" is the loosest commitment on the page.
    for (const list of m.values()) {
      list.sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));
    }
    return m;
  }, [events]);

  const moveMonth = useCallback((dir: -1 | 1) => {
    setSelectedDay(null);
    setMonthCursor((c) => {
      const d = new Date(c);
      d.setMonth(d.getMonth() + dir);
      return d;
    });
  }, []);

  const dayEvents = selectedDay ? (byDay.get(toISO(selectedDay)) ?? []) : [];

  return (
    <div className={`sched${view === "month" ? " sched-wide" : ""}`}>
      <div className="sched-hd">
        <div>
          <div className="eyebrow">Hunting team</div>
          <h1 className="sched-title serif">Schedule</h1>
        </div>
        <button className="chip active" type="button" onClick={() => setCreating(true)}>
          New event
        </button>
      </div>

      <div className="sched-filters">
        <div className="audit-filter-group" role="group" aria-label="Which events">
          <button
            type="button"
            className={`chip${view === "upcoming" ? " active" : ""}`}
            aria-pressed={view === "upcoming"}
            onClick={() => setView("upcoming")}
          >
            Upcoming
          </button>
          <button
            type="button"
            className={`chip${view === "month" ? " active" : ""}`}
            aria-pressed={view === "month"}
            onClick={() => setView("month")}
          >
            Month
          </button>
          <button
            type="button"
            className={`chip${view === "past" ? " active" : ""}`}
            aria-pressed={view === "past"}
            onClick={() => setView("past")}
          >
            Past
          </button>
        </div>
        {view === "upcoming" && pending > 0 && (
          <span className="sched-pending-note">
            {pending === 1 ? "1 event needs your answer" : `${pending} events need your answer`}
          </span>
        )}
      </div>

      {notice && (
        <p className="sched-notice" role="status">
          {notice}
          <button type="button" className="sched-link" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </p>
      )}

      {error && <p className="audit-empty">{error}</p>}
      {loading && !error && <p className="audit-empty">Loading…</p>}

      {/* Month keeps its grid even with nothing in it — an empty November is a
          real answer, and swapping it for a message would hide the nav that
          gets you to a month that isn't empty. */}
      {!loading && !error && view === "month" && (
        <MonthGrid
          cursor={monthCursor}
          byDay={byDay}
          selectedDay={selectedDay}
          onMoveMonth={moveMonth}
          onSelectDay={setSelectedDay}
        />
      )}

      {!loading && !error && view !== "month" && events.length === 0 && (
        <div className="empty-day">
          <div className="glyph">Nothing planned.</div>
          <div>
            {view === "upcoming"
              ? "No upcoming events. Create the first one."
              : "No events in the last year."}
          </div>
        </div>
      )}

      {!loading && !error && view !== "month" &&
        events.map((e) => (
          <EventRow
            key={e.eventId}
            event={e}
            past={view === "past"}
            onChanged={replace}
            onDeleted={remove}
            onPendingChanged={onPendingChanged}
            onEdit={() => setEditing(e)}
            onCopy={() => setCopying(e)}
          />
        ))}

      {/* Picking a day drops its Events in below the grid as the same rows the
          list view uses — so answering works identically wherever you found
          the Event, and there is one EventRow to keep correct, not two. */}
      {!loading && !error && view === "month" && selectedDay && (
        <div className="sched-day-feed">
          <h2 className="sched-day-feed-hd serif">
            {selectedDay.toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </h2>
          {dayEvents.length === 0 ? (
            <p className="audit-empty">Nothing planned for this day.</p>
          ) : (
            dayEvents.map((e) => (
              <EventRow
                key={e.eventId}
                event={e}
                past={isBeforeToday(selectedDay)}
                // The heading above already says which day this is; four
                // identical date blocks under it are noise.
                showDate={false}
                onChanged={replace}
                onDeleted={remove}
                onPendingChanged={onPendingChanged}
                onEdit={() => setEditing(e)}
                onCopy={() => setCopying(e)}
              />
            ))
          )}
        </div>
      )}

      {(creating || editing) && (
        <EventForm
          event={editing}
          kinds={kinds}
          onSaved={onSaved}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {copying && (
        <CopyEventDialog
          event={copying}
          onCopied={(created) => {
            setCopying(null);
            // Every copy starts unanswered, so the badge owes exactly as many
            // more as were made.
            onPendingChanged();
            void refresh();
            setNotice(
              created.length === 1
                ? `Copied to ${fmtDay(created[0].date)}.`
                : `Copied to ${created.length} days — ${fmtDay(created[0].date)} to ` +
                  `${fmtDay(created[created.length - 1].date)}.`,
            );
          }}
          onClose={() => setCopying(null)}
        />
      )}
    </div>
  );
}

/**
 * The month grid. Same card, header and Mon-first day row as the photo
 * Calendar's `.cal` sidebar, so the two read as one product — but the cells are
 * tall and wide instead of square, because these hold titles rather than a
 * density bar. The grid keeps a floor of 168px per column and scrolls
 * horizontally below that, which is what actually guarantees a title stays
 * legible instead of collapsing to an ellipsis on a narrow window.
 */
function MonthGrid({
  cursor,
  byDay,
  selectedDay,
  onMoveMonth,
  onSelectDay,
}: {
  cursor: Date;
  byDay: Map<string, ScheduleEvent[]>;
  selectedDay: Date | null;
  onMoveMonth: (dir: -1 | 1) => void;
  onSelectDay: (d: Date) => void;
}) {
  const today = new Date();
  const first = startOfMonth(cursor);
  const year = first.getFullYear();
  const month = first.getMonth();
  // Mon-first, matching the photo Calendar: Sun=0 becomes 6, Mon=1 becomes 0.
  const startDow = (first.getDay() + 6) % 7;
  const totalDays = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));

  return (
    <div className="sched-cal">
      <div className="cal-hd">
        <div>
          <div className="month serif">
            {cursor.toLocaleDateString(undefined, { month: "long" })}
          </div>
          <div className="year">{year}</div>
        </div>
        <div className="cal-nav">
          <button type="button" onClick={() => onMoveMonth(-1)} aria-label="Previous month">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button type="button" onClick={() => onMoveMonth(1)} aria-label="Next month">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="sched-cal-scroll">
        <div className="sched-cal-grid">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div className="cal-dow" key={d}>
              {d}
            </div>
          ))}
          {cells.map((date, i) => {
            if (!date) return <div className="sched-cal-day empty" key={`e${i}`} />;
            const dayEvents = byDay.get(toISO(date)) ?? [];
            const shown = dayEvents.slice(0, PILLS_PER_DAY);
            const hidden = dayEvents.length - shown.length;
            const classes = [
              "sched-cal-day",
              sameDay(date, today) ? "today" : "",
              selectedDay && sameDay(date, selectedDay) ? "selected" : "",
              dayEvents.length > 0 ? "has-events" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={toISO(date)}
                type="button"
                className={classes}
                onClick={() => onSelectDay(date)}
                aria-label={`${date.toDateString()} — ${dayEvents.length} event${
                  dayEvents.length === 1 ? "" : "s"
                }`}
              >
                <span className="sched-cal-num">{date.getDate()}</span>
                <span className="sched-cal-pills">
                  {shown.map((e) => (
                    <span
                      key={e.eventId}
                      className={`sched-cal-pill ${pillTone(e)}`}
                      // The visible text is clipped to the cell; the full title
                      // is always one hover away.
                      title={`${e.title}${e.startTime ? ` — ${e.startTime.slice(0, 5)}` : ""}`}
                    >
                      {e.startTime && (
                        <span className="sched-cal-pill-time">{e.startTime.slice(0, 5)}</span>
                      )}
                      {e.title}
                    </span>
                  ))}
                  {hidden > 0 && <span className="sched-cal-more">+{hidden} more</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="sched-cal-legend">
        <span><span className="sched-dot yes" /> You're coming</span>
        <span><span className="sched-dot no" /> You can't</span>
        <span><span className="sched-dot pending" /> Needs your answer</span>
      </div>
    </div>
  );
}

/**
 * A pill is coloured by <em>your</em> answer, not by the Event's popularity.
 * The question this grid exists to answer is "what do I still owe someone?",
 * so the unanswered ones are the ones that should catch the eye.
 */
function pillTone(e: ScheduleEvent): string {
  if (e.cancelled) return "cancelled";
  if (!e.myResponse) return "pending";
  return e.myResponse.answer === "yes" ? "yes" : "no";
}

function EventRow({
  event,
  past,
  showDate = true,
  onChanged,
  onDeleted,
  onPendingChanged,
  onEdit,
  onCopy,
}: {
  event: ScheduleEvent;
  past: boolean;
  /** False under a heading that already names the day — the month day feed. */
  showDate?: boolean;
  onChanged: (e: ScheduleEvent) => void;
  onDeleted: (e: ScheduleEvent) => void;
  onPendingChanged: () => void;
  onEdit: () => void;
  onCopy: () => void;
}) {
  const { isAdmin } = useAccess();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWho, setShowWho] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Which answer's comment box is open — null when closed. It holds the answer
  // rather than a boolean because saving re-PUTs the answer alongside the
  // comment, and the box is worded differently for a yes than for a no.
  const [commenting, setCommenting] = useState<Answer | null>(null);
  const [commentDraft, setCommentDraft] = useState("");

  const day = parseLocalDate(event.date);
  const mine = event.myResponse?.answer ?? null;

  // Invited, no answer — the server sends both lists so this is a set
  // difference rather than a third round trip. Matches the server's
  // pendingCount by construction, since it computes the same difference.
  const awaiting = useMemo(() => {
    const answered = new Set(event.responses.map((r) => r.userId));
    return event.invitees.filter((i) => !answered.has(i.userId));
  }, [event.invitees, event.responses]);

  /**
   * Answering also offers the comment box, so "coming — bringing the dog" is
   * one gesture rather than two. The answer is recorded before the box opens:
   * a yes that only counts once you also press Save would lose the answer of
   * anyone who types nothing, which is most people.
   *
   * <p>Clicking the answer you already gave re-opens that box on what you
   * wrote, which is how a comment is edited. It no longer withdraws the
   * answer — undo moved inside the box, because the button now has a use for
   * people who are not changing their mind, and taking a whole answer back by
   * accident while reaching for the comment is the worse mistake.
   */
  async function answer(a: Answer) {
    if (busy) return;
    if (mine === a) {
      // Second press on your own answer: a toggle onto the comment, seeded
      // from the server's copy so editing starts from what is actually stored.
      setCommentDraft(event.myResponse?.comment ?? "");
      setCommenting((open) => (open === a ? null : a));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Keep any comment already attached — switching yes to no should
      // change the answer, not silently drop what you wrote alongside it.
      const kept = event.myResponse?.comment ?? null;
      onChanged(await api.answerEvent(event.eventId, a, kept));
      setCommentDraft(kept ?? "");
      setCommenting(a);
      // Answering counts the Event down off your pending list.
      onPendingChanged();
    } catch {
      setError("Could not record your answer.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Save the comment against the answer the box was opened for. Blank saves as
   * no comment rather than an empty string — the server does the same
   * normalisation, so clearing the text is how a comment is deleted.
   */
  async function saveComment() {
    if (busy || !commenting) return;
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.answerEvent(event.eventId, commenting, commentDraft.trim() || null));
      setCommenting(null);
    } catch {
      setError("Could not save your comment.");
    } finally {
      setBusy(false);
    }
  }

  /** Undo, from inside the comment box — back to no answer at all. */
  async function withdrawAnswer() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.withdrawAnswer(event.eventId));
      setCommenting(null);
      setCommentDraft("");
      // Taking the answer back puts the Event straight back on your list.
      onPendingChanged();
    } catch {
      setError("Could not undo your answer.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Destroy the Event, its invites and every answer. No optimistic removal and
   * no undo: the row is only taken off the screen once the server says it is
   * gone, because a row that reappears after a failed delete is worse than a
   * moment's wait.
   */
  async function confirmDelete() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onDeleted(await api.deleteEvent(event.eventId));
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Only an administrator can delete an event."
          : "Could not delete the event.",
      );
      setBusy(false);
    }
  }

  async function confirmCancel() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.cancelEvent(event.eventId, cancelReason.trim() || null));
      setCancelling(false);
      // A cancelled Event drops out of the pending count for everyone.
      onPendingChanged();
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Only the person who created this event, or an admin, can cancel it."
          : "Could not cancel the event.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`sched-ev${event.cancelled ? " cancelled" : ""}`}>
      {showDate && (
        <div className="sched-when" aria-hidden="true">
          <div className="sched-dow">{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
          <div className="sched-day">{day.getDate()}</div>
          <div className="sched-mon">{day.toLocaleDateString(undefined, { month: "short" })}</div>
        </div>
      )}

      <div className="sched-body">
        <div className="sched-ev-hd">
          <span className="sched-kind">{event.kind}</span>
          <h3 className="sched-ev-title">{event.title}</h3>
          {event.cancelled && <span className="sched-cancelled-tag">Cancelled</span>}
        </div>

        <div className="sched-meta">
          <span>
            <time dateTime={event.date}>
              {day.toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </time>
          </span>
          {event.startTime && <span>Gathering {event.startTime.slice(0, 5)}</span>}
          {event.locationText && <span>{event.locationText}</span>}
          <span className="sched-by">Arranged by {event.createdBy}</span>
        </div>

        {event.description && <p className="sched-desc">{event.description}</p>}

        {event.cancelled && event.cancelReason && (
          <p className="sched-reason">Cancelled — {event.cancelReason}</p>
        )}

        <div className="sched-tally">
          <button
            type="button"
            className="sched-tally-toggle"
            onClick={() => setShowWho((s) => !s)}
            aria-expanded={showWho}
            // The three counts are separate spans spaced by flex gap, so the
            // accessible name would otherwise run together as "2 yes0 no1
            // pending".
            aria-label={
              `${event.yesCount} coming, ${event.noCount} can't, ` +
              `${event.pendingCount} yet to answer. Show who.`
            }
          >
            <span className="sched-count yes">{event.yesCount} yes</span>
            <span className="sched-count no">{event.noCount} no</span>
            {/* Pending is the absence of a Response, computed against the
                current team — so it can move without anyone answering. */}
            <span className="sched-count pending">{event.pendingCount} pending</span>
          </button>

          {/* aria-pressed reports which answer is yours. A pressed button is
              still live: it re-opens the comment box, which aria-expanded says
              and the title spells out. */}
          {!event.cancelled && !past && (
            <div className="sched-answer" role="group" aria-label="Your answer">
              <button
                type="button"
                className={`chip${mine === "yes" ? " active commentable" : ""}`}
                aria-pressed={mine === "yes"}
                aria-expanded={mine === "yes" ? commenting === "yes" : undefined}
                disabled={busy}
                title={
                  mine === "yes"
                    ? "Click again to add or edit your comment"
                    : undefined
                }
                onClick={() => void answer("yes")}
              >
                I'm coming
              </button>
              <button
                type="button"
                className={`chip${mine === "no" ? " active commentable" : ""}`}
                aria-pressed={mine === "no"}
                aria-expanded={mine === "no" ? commenting === "no" : undefined}
                disabled={busy}
                title={
                  mine === "no"
                    ? "Click again to add or edit your comment"
                    : undefined
                }
                onClick={() => void answer("no")}
              >
                Can't make it
              </button>
            </div>
          )}

          <div className="sched-owner-actions">
            {/* Copy needs no claim on the source — a copy is a new Event you
                will own — so unlike Edit and Cancel it is offered to everyone,
                including on days that are cancelled or already past. Copying
                last Saturday onto next Saturday is a normal way to plan. */}
            <button type="button" className="sched-link" onClick={onCopy}>
              Copy
            </button>
            {event.canEdit && !event.cancelled && (
              <>
                <button type="button" className="sched-link" onClick={onEdit}>
                  Edit
                </button>
                <button type="button" className="sched-link" onClick={() => setCancelling(true)}>
                  Cancel event
                </button>
              </>
            )}
            {/* Admin only, and offered on cancelled and past Events too — a
                duplicate typed onto the wrong day is worth removing whatever
                state it is in. Styled apart from its neighbours because it is
                the one control here that destroys something. */}
            {isAdmin && (
              <button
                type="button"
                className="sched-link sched-link-danger"
                onClick={() => setDeleting(true)}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {cancelling && (
          <div className="sched-cancel-box">
            <label className="ev-field">
              <span>Why is it off?</span>
              <input
                type="text"
                maxLength={500}
                autoFocus
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Weather, too few people…"
              />
            </label>
            <div className="ev-actions">
              <button type="button" className="chip" onClick={() => setCancelling(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="chip active"
                disabled={busy}
                onClick={() => void confirmCancel()}
              >
                Cancel event
              </button>
            </div>
          </div>
        )}

        {/* Names what will be destroyed rather than asking "are you sure?".
            The answers are the part nobody can put back, so the count of them
            is the fact that should decide it — and Cancel is offered here as
            the thing most people actually meant. */}
        {deleting && (
          <div className="sched-cancel-box sched-delete-box">
            <p className="sched-delete-warn">
              Delete “{event.title}” on {fmtDay(event.date)}?
              {event.responses.length > 0 && (
                <>
                  {" "}
                  This also destroys{" "}
                  {event.responses.length === 1
                    ? "1 answer"
                    : `${event.responses.length} answers`}{" "}
                  from {event.responses.map((r) => r.name).join(", ")}.
                </>
              )}{" "}
              This cannot be undone.
            </p>
            <div className="ev-actions">
              <button type="button" className="chip" onClick={() => setDeleting(false)}>
                Keep it
              </button>
              {!event.cancelled && (
                <button
                  type="button"
                  className="chip"
                  disabled={busy}
                  onClick={() => {
                    setDeleting(false);
                    setCancelling(true);
                  }}
                >
                  Cancel it instead
                </button>
              )}
              <button
                type="button"
                className="chip danger"
                disabled={busy}
                onClick={() => void confirmDelete()}
              >
                {busy ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        )}

        {/* Opened by answering, and re-opened by pressing your own answer
            again. Deliberately not a dialog: it sits under the buttons that
            opened it, so the answer it belongs to stays on screen and stays
            readable while you type. */}
        {commenting && (
          <div className="sched-cancel-box sched-comment-box">
            <label className="ev-field">
              <span>
                {commenting === "yes"
                  ? "Anything to add? (optional)"
                  : "Why can't you make it? (optional)"}
              </span>
              <input
                type="text"
                // Matches @Size(max = 500) on ScheduleAnswerRequest.comment —
                // the field should stop rather than the save failing.
                maxLength={500}
                autoFocus
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder={
                  commenting === "yes"
                    ? "Bringing the dog, arriving late…"
                    : "Away that weekend…"
                }
                // Enter saves: this is a single line, so there is nothing else
                // it could usefully do, and Escape closes without saving.
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void saveComment();
                  } else if (e.key === "Escape") {
                    setCommenting(null);
                  }
                }}
              />
            </label>
            <div className="ev-actions sched-comment-actions">
              {/* Undo lives here now that pressing your answer again edits the
                  comment instead. Kept apart from the two chips, and worded as
                  what it does, so it cannot be hit while reaching for Save. */}
              <button
                type="button"
                className="sched-link sched-link-danger"
                disabled={busy}
                onClick={() => void withdrawAnswer()}
              >
                Take my answer back
              </button>
              <button type="button" className="chip" onClick={() => setCommenting(null)}>
                Close
              </button>
              <button
                type="button"
                className="chip active"
                disabled={busy}
                onClick={() => void saveComment()}
              >
                {busy ? "Saving…" : "Save comment"}
              </button>
            </div>
          </div>
        )}

        {showWho && (
          <ul className="sched-who">
            {event.responses.length === 0 && awaiting.length === 0 && (
              <li className="sched-who-empty">Nobody is invited to this event.</li>
            )}
            {event.responses.map((r) => (
              <li key={r.userId}>
                <span className="sched-who-person">
                  <span className={`sched-dot ${r.answer}`} aria-hidden="true" />
                  <span className="sched-who-name">{r.name}</span>
                  <span className="sched-who-answer">
                    {r.answer === "yes" ? "coming" : "can't"}
                  </span>
                </span>
                <span className="sched-who-comment">{r.comment && <>“{r.comment}”</>}</span>
              </li>
            ))}
            {/* Now that an Event names who was asked, Pending can be a list of
                people rather than a number. "Who am I still waiting on?" is the
                question the organiser of a hunt actually has, and a count of
                three never answered it. */}
            {awaiting.map((m) => (
              <li key={m.userId}>
                <span className="sched-who-person">
                  <span className="sched-dot pending" aria-hidden="true" />
                  <span className="sched-who-name">{m.name}</span>
                  <span className="sched-who-answer">yet to answer</span>
                </span>
                <span className="sched-who-comment" />
              </li>
            ))}
          </ul>
        )}

        {error && <p className="sched-error">{error}</p>}
      </div>
    </article>
  );
}

// Dates on the wire are calendar days, not instants. new Date("2026-08-09")
// parses as UTC midnight and renders as the 8th west of Greenwich, so the
// parts are split by hand — the same reason router.ts does it.
function parseLocalDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function toISO(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function todayISO(): string {
  return toISO(new Date());
}

function yesterdayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISO(d);
}

function aYearAgoISO(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return toISO(d);
}

/**
 * Whether a day is already over, which is what decides if the answer buttons
 * are offered. Compared as calendar days rather than instants: an Event at
 * 07:00 is still answerable at 06:00 on the same morning.
 */
function isBeforeToday(d: Date): boolean {
  return toISO(d) < toISO(new Date());
}

/** Short human day for the post-copy confirmation ("Wed 12 Aug"). */
function fmtDay(iso: string): string {
  return parseLocalDate(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}
